/**
 * SHA-256 integrity hashing (D-030).
 *
 * `crypto.subtle.digest` has NO streaming form: it takes a complete
 * `ArrayBuffer`, so hashing a blob materialises that blob's bytes. It must not
 * be claimed that a backup is never held in memory. What the design does
 * instead is BOUND the peak — blobs are read and hashed one at a time, so the
 * digest peak is the size of the largest single photo rather than the whole
 * library.
 */

export class ChecksumUnavailableError extends Error {
  constructor() {
    super(
      'Secure hashing is unavailable in this browser context. Project Sola needs a secure (HTTPS) origin to verify backup integrity.',
    )
    this.name = 'ChecksumUnavailableError'
  }
}

export function isChecksumAvailable(): boolean {
  return typeof crypto !== 'undefined' && typeof crypto.subtle?.digest === 'function'
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, '0')).join('')
}

export async function sha256(bytes: Uint8Array): Promise<string> {
  if (!isChecksumAvailable()) throw new ChecksumUnavailableError()
  // Copy into a fresh buffer so a view over a larger pool hashes only its own
  // bytes.
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return toHex(await crypto.subtle.digest('SHA-256', copy.buffer))
}

export async function sha256OfText(text: string): Promise<string> {
  return sha256(new TextEncoder().encode(text))
}

/**
 * Hash one blob.
 *
 * Deliberately takes a single blob rather than a list: callers must iterate so
 * that only one blob is resident at a time.
 */
export async function sha256OfBlob(blob: Blob): Promise<string> {
  return sha256(new Uint8Array(await blob.arrayBuffer()))
}
